import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

/**
 * Monitors user activity and automatically logs out after a period of inactivity.
 * Listens to mouse, keyboard, touch, and scroll events to detect activity.
 */
@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService implements OnDestroy {

  /** Inactivity timeout in milliseconds (default: 30 minutes) */
  private readonly IDLE_TIMEOUT_MS = 30 * 60 * 1000;

  /** How often to check for idle state (every 60 seconds) */
  private readonly CHECK_INTERVAL_MS = 60 * 1000;

  private lastActivityTimestamp = Date.now();
  private checkIntervalId: ReturnType<typeof setInterval> | null = null;
  private boundResetTimer: () => void;
  private isRunning = false;

  private readonly ACTIVITY_EVENTS: string[] = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'click'
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone
  ) {
    this.boundResetTimer = this.resetTimer.bind(this);

    // Start monitoring when user is authenticated
    this.authService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth) {
        this.startMonitoring();
      } else {
        this.stopMonitoring();
      }
    });
  }

  private startMonitoring(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastActivityTimestamp = Date.now();

    // Add event listeners outside Angular zone to avoid triggering change detection
    this.ngZone.runOutsideAngular(() => {
      this.ACTIVITY_EVENTS.forEach(event => {
        document.addEventListener(event, this.boundResetTimer, { passive: true });
      });

      this.checkIntervalId = setInterval(() => {
        this.checkIdle();
      }, this.CHECK_INTERVAL_MS);
    });
  }

  private stopMonitoring(): void {
    if (!this.isRunning) return;
    this.isRunning = false;

    this.ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, this.boundResetTimer);
    });

    if (this.checkIntervalId !== null) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  private resetTimer(): void {
    this.lastActivityTimestamp = Date.now();
  }

  private checkIdle(): void {
    const elapsed = Date.now() - this.lastActivityTimestamp;

    if (elapsed >= this.IDLE_TIMEOUT_MS) {
      this.ngZone.run(() => {
        this.handleTimeout();
      });
    }
  }

  private handleTimeout(): void {
    this.stopMonitoring();
    this.authService.logout();
    this.router.navigate(['/login'], {
      queryParams: { reason: 'idle_timeout' }
    });
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
  }
}
